// ===================================================================
// KAIXO - Sonido de notificación de mensaje
// ===================================================================
// Tono "ping" sintetizado con Web Audio API. NO carga ningún asset
// (sin .mp3 / .wav) — dos pulsos seno cortos y agudos descendentes,
// estilo notificación corporativa neutra. Funciona en navegador y en
// Capacitor (WebView usa el mismo motor).
//
// Restricciones del navegador:
//   - AudioContext NO se puede instanciar hasta que el usuario haya
//     interactuado con la página al menos una vez. Como en KAIXO eso
//     ocurre en el login/onboarding, lo lazy-creamos en el primer play
//     y rehidratamos si está "suspended".
//
// Cuando se reciba un mensaje y la app esté en background, el sonido
// del SO lo dispara el push (FCM/APNs/Web Push), NO esta utility —
// porque el JS de la pestaña está pausado o el socket suspendido.
// ===================================================================

let ctx = null;
let lastPlayedAt = 0;
const MIN_INTERVAL_MS = 700;     // anti-spam: no más de un beep por chunk
let muted = false;               // override global (silenciar la app)

function getCtx() {
    if (!ctx) {
        try {
            const C = window.AudioContext || window.webkitAudioContext;
            if (!C) return null;
            ctx = new C();
        } catch { return null; }
    }
    if (ctx && ctx.state === 'suspended') {
        try { ctx.resume().catch(() => {}); } catch {}
    }
    return ctx;
}

/**
 * Reproduce el ping. Idempotente: si se llama varias veces en menos de
 * MIN_INTERVAL_MS, solo suena la primera (por ej. ráfaga de mensajes).
 */
export function playMessageSound() {
    if (muted) return;
    // Si el usuario silenció la app vía persistencia (settings)
    try {
        if (localStorage.getItem('kaixo_msg_sound_muted') === '1') return;
    } catch {}

    const now = Date.now();
    if (now - lastPlayedAt < MIN_INTERVAL_MS) return;
    lastPlayedAt = now;

    const c = getCtx();
    if (!c) return;

    const t = c.currentTime;
    // Dos pulsos descendentes — frecuencia y duración elegidas para
    // que suene "claro pero no estridente" en altavoces de móvil.
    [{ f: 880, t0: 0,    dur: 0.13 },
     { f: 660, t0: 0.09, dur: 0.16 }].forEach(({ f, t0, dur }) => {
        const o = c.createOscillator();
        const g = c.createGain();
        o.type = 'sine';
        o.frequency.value = f;
        const start = t + t0;
        g.gain.setValueAtTime(0, start);
        g.gain.linearRampToValueAtTime(0.20, start + 0.008);
        g.gain.exponentialRampToValueAtTime(0.0008, start + dur);
        o.connect(g).connect(c.destination);
        o.start(start);
        o.stop(start + dur + 0.02);
    });
}

/**
 * Silenciar / desilenciar globalmente. Útil para una opción "silenciar
 * sonidos de mensajes" en ajustes.
 */
export function setMessageSoundMuted(value) {
    muted = !!value;
    try {
        localStorage.setItem('kaixo_msg_sound_muted', value ? '1' : '0');
    } catch {}
}

export function isMessageSoundMuted() {
    if (muted) return true;
    try { return localStorage.getItem('kaixo_msg_sound_muted') === '1'; }
    catch { return false; }
}

/**
 * Cierra el AudioContext y libera recursos del navegador. Usar al
 * cerrar sesión / unmount global de la app. WebView Android suele
 * mantener el contexto abierto si no lo cerramos explícitamente.
 */
export function closeMessageSoundContext() {
    if (!ctx) return;
    try {
        if (typeof ctx.close === 'function') ctx.close().catch(() => {});
    } catch {}
    ctx = null;
    lastPlayedAt = 0;
}
